import FormCheckboxGroup from '@atoms/FormCheckboxGroup/FormCheckboxGroup';
import Icon from '@atoms/Icon/Icon';
import { IListingFilterValue } from '@models/IListingFilter';
import SizeEnum from '@models/enums/SizeEnum';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Props {
  readonly filtersCloseLabel: string;
  readonly groupKey: string;
  readonly helpText: string;
  readonly label: string;
  readonly openByDefault: boolean;
  readonly values: IListingFilterValue[];
  readonly showExpandButton: boolean;
}

const FilterGroup = ({
  filtersCloseLabel,
  groupKey = '',
  helpText,
  label = '',
  openByDefault,
  values,
  showExpandButton,
}: Props) => {
  const [isFilterGroupOpen, setIsFilterGroupOpen] = useState(openByDefault);
  const [isHelpTextOpen, setIsHelpTextOpen] = useState(false);

  const accordionTransition = {
    damping: 20,
    duration: 0.4,
    mass: 1.2,
    stiffness: 80,
    type: 'spring',
  };

  const [height, setHeight] = useState(isFilterGroupOpen ? 'auto' : 0);
  const [opacity, setOpacity] = useState(isFilterGroupOpen ? 1 : 0);

  useEffect(() => {
    setHeight(isFilterGroupOpen ? 'auto' : 0);
    setOpacity(isFilterGroupOpen ? 1 : 0);
  }, [isFilterGroupOpen]);

  return (
    <div
      key={`${groupKey}-facet-${label}`}
      className="select-none"
      data-testid="Filter-Group"
    >
      <div className="mb-8 flex min-h-[40px] items-center space-x-3">
        <button
          className="flex max-w-[80%] flex-row items-center"
          onClick={() => setIsFilterGroupOpen(!isFilterGroupOpen)}
          type="button"
        >
          <span
            className="h4 text-left text-black-100 transition-colors duration-200"
            data-testid="Filter-Group-Label"
          >
            {label}
          </span>
        </button>

        <AnimatePresence>
          {isFilterGroupOpen && (
            <>
              {helpText && (
                <motion.button
                  animate={{ opacity: 1 }}
                  className={classNames(
                    isHelpTextOpen
                      ? 'bg-black-100 text-white'
                      : 'bg-grey text-black-100',
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-normal text-lg transition-colors duration-200 hover:bg-black-100 hover:text-white'
                  )}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  onClick={() => setIsHelpTextOpen(!isHelpTextOpen)}
                  type="button"
                >
                  ?
                </motion.button>
              )}
              {showExpandButton && (
                <motion.button
                  animate={{ opacity: 1, rotate: 0 }}
                  className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow md:flex"
                  exit={{ opacity: 0, rotate: 180 }}
                  initial={{ opacity: 0, rotate: 180 }}
                  onClick={() => setIsFilterGroupOpen(!isFilterGroupOpen)}
                  type="button"
                >
                  <Icon name="caret_down" />
                </motion.button>
              )}
            </>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {isHelpTextOpen && isFilterGroupOpen && helpText && (
          <motion.div
            animate={{ height: 'auto', opacity: 1 }}
            exit={{
              height: 0,
              opacity: 0,
            }}
            initial={{ height: 0, opacity: 0 }}
            transition={accordionTransition}
          >
            <div className="flex flex-row">
              <span dangerouslySetInnerHTML={{ __html: helpText }} />

              <span>&nbsp;</span>

              <button
                className="underline"
                onClick={() => setIsHelpTextOpen(!isHelpTextOpen)}
                type="button"
              >
                {filtersCloseLabel || 'Close'}
              </button>
            </div>

            <div className="h-8" role="presentation" />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isFilterGroupOpen && (
          <motion.div
            animate={{ height: 'auto', opacity: 1 }}
            initial={{ height, opacity }}
            exit={{ height: 0, opacity: 0 }}
            transition={accordionTransition}
            data-testid="filter-group-open"
          >
            <FormCheckboxGroup
              groupClassName="mb-5 flex flex-col space-y-7"
              checkboxClassName="px-4"
              name={groupKey}
              options={values.map((currentValue) => ({
                label: `${currentValue.label} ${
                  currentValue.total >= 0 && `(${currentValue.total})`
                }`,
                value: currentValue.key,
              }))}
              checkboxLabelType={SizeEnum.Large}
            />
            <div className="h-3" role="presentation" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FilterGroup;
